import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { Icon } from '../Icon'
import { overloadColor } from '../util/style'
import {
  AlignHorizontal,
  AlignVertical,
  PerToastOptions,
  Toast,
  ToastIcons,
  ToastPosition,
  ToastType,
} from './types'

interface ToastWrapperProps {
  width: number
  height: number
  padding: string
  alignHorizontal: AlignHorizontal
  alignVertical: AlignVertical
  iconWidth: number
  color: string
  background: string
  borderRadius: string
  boxShadow: string
  showIcon: boolean
  showCloseIcon: boolean
}
const ToastWrapper = styled.div<ToastWrapperProps>`
  position: fixed;
  z-index: 11000;
  width: ${props => props.width}px;
  height: ${props => (props.height ? `${props.height}px` : 'auto')};

  display: grid;
  grid-template-columns:
    ${props => props.showIcon && 'auto'}
    1fr
    ${props => props.showCloseIcon && 'auto'};

  background: ${props => overloadColor(props.background)};
  border-radius: ${props => props.borderRadius};
  box-shadow: ${props => props.boxShadow};
  overflow: hidden;

  div.icon {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    width: ${props => props.iconWidth}px;
    height: ${props =>
      props.height ? `${props.height}px` : `${props.iconWidth}px`};

    font-size: 2rem;
  }
  div.content {
    display: flex;
    justify-content: ${props =>
      props.alignHorizontal === 'right'
        ? 'flex-end'
        : props.alignHorizontal === 'center'
        ? 'center'
        : 'flex-start'};
    align-items: ${props =>
      props.alignVertical === 'bottom'
        ? 'flex-end'
        : props.alignVertical === 'center'
        ? 'center'
        : 'flex-start'};

    height: ${props => (props.height ? `${props.height}px` : 'auto')};
    padding: ${props => props.padding};

    font-size: 0.8rem;
    color: ${props => overloadColor(props.color)};
    user-select: none;
  }
  div.close-icon {
    display: flex;
    justify-content: center;
    width: 32px;
    padding-top: 0.5rem;
  }

  ${props => props.theme.media.mobile} {
    width: calc(100vw - 1rem);
    right: 0.5rem;
    bottom: 0.5rem;
  }
`
const AnimatedToast = motion(ToastWrapper)

const progressBarAnimation = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
`

interface ProgressBarProps {
  life: number
  color: string
  height: string
}
const ProgressBar = styled.div<ProgressBarProps>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: ${props => props.height};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: ${props => props.color};
    animation: ${progressBarAnimation} ${props => props.life}ms;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }
`

type IndexedToast = Toast & { index: number }

interface ToastContainerProps {
  toasts: Toast[]
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts }) => {
  const indexedToasts = useMemo<IndexedToast[]>(() => {
    const newToasts = [...toasts]
    const indexes: Partial<Record<ToastPosition, number>> = {}

    for (const toast of newToasts) {
      indexes[toast.position] = (indexes[toast.position] ?? -1) + 1
      ;(toast as IndexedToast).index = indexes[toast.position] ?? 0
    }

    return newToasts as IndexedToast[]
  }, [toasts])

  const toastIcon = useCallback((type: ToastType, icons: ToastIcons | null) => {
    if (!icons?.[type]) return null

    switch (type) {
      case 'success':
        return (
          <Icon icon="check" color="success" fixedWidth {...icons.success} />
        )
      case 'error':
        return <Icon icon="times" color="error" fixedWidth {...icons.error} />
      case 'warning':
        return (
          <Icon
            type="solid"
            icon="exclamation"
            color="warning"
            fixedWidth
            {...icons.warning}
          />
        )
      case 'info':
        return (
          <Icon
            type="solid"
            icon="info"
            color="info"
            fixedWidth
            {...icons.info}
          />
        )
    }
  }, [])

  const getToastColor = useCallback(
    (type: ToastType, colors: string | PerToastOptions<string>) => {
      if (typeof colors === 'string') return colors
      return colors[type]
    },
    []
  )

  const toastPosition = useCallback((position: ToastPosition) => {
    const margin = '1rem'
    const pos = (
      top: string | null,
      right: string | null,
      bottom: string | null,
      left: string | null
    ) => ({ top, right, bottom, left })

    switch (position) {
      case 'top-left':
        return pos(margin, null, null, margin)
      case 'top-center':
        return pos(margin, null, null, '50%')
      case 'top-right':
        return pos(margin, margin, null, null)
      case 'center-left':
        return pos('50%', null, null, margin)
      case 'center-center':
        return pos('50%', null, null, '50%')
      case 'center-right':
        return pos('50%', margin, null, null)
      case 'bottom-left':
        return pos(null, null, margin, margin)
      case 'bottom-center':
        return pos(null, null, margin, '50%')
      case 'bottom-right':
        return pos(null, margin, margin, null)
    }
  }, [])

  const toastInitial = useCallback(
    (position: ToastPosition, height: number): [x: string, y: string] => {
      switch (position) {
        case 'top-left':
          return ['0%', -height + 'px']
        case 'top-center':
          return ['50%', -height + 'px']
        case 'top-right':
          return ['100%', -height + 'px']
        case 'center-left':
          return ['0%', `calc(50% - ${height / 2}px)`]
        case 'center-center':
          return ['50%', `calc(50% - ${height / 2}px)`]
        case 'center-right':
          return ['100%', `calc(50% - ${height / 2}px)`]
        case 'bottom-left':
          return ['0%', `calc(100% + ${height}px)`]
        case 'bottom-center':
          return ['50%', `calc(100% + ${height}px)`]
        case 'bottom-right':
          return ['100%', `calc(100% + ${height}px)`]
      }
    },
    []
  )

  const toastTarget = useCallback(
    (
      position: ToastPosition,
      height: number,
      index: number
    ): { x: string; y: string; tx: string; ty: string } => {
      let values: [x: number, y: string, tx: string, ty: string] = [
        0,
        '0%',
        '0',
        '0',
      ]
      const offset = (n: number, off: number) => `calc(${n}% + ${off}px)`
      const calc = (y: number, up: boolean) =>
        `calc(${y}% + ${(index * height + index * 12) * (up ? -1 : 1)}px)`

      switch (position) {
        case 'top-left':
          values = [0, calc(0, false), offset(0, 12), offset(0, 12)]
          break
        case 'top-center':
          values = [50, calc(0, false), offset(-50, 0), offset(0, 12)]
          break
        case 'top-right':
          values = [100, calc(0, false), offset(-100, -12), offset(0, 12)]
          break
        case 'center-left':
          values = [0, calc(50, false), offset(0, 12), offset(-50, 0)]
          break
        case 'center-center':
          values = [50, calc(50, false), offset(-50, 0), offset(-50, 0)]
          break
        case 'center-right':
          values = [100, calc(50, false), offset(-100, -12), offset(-50, 0)]
          break
        case 'bottom-left':
          values = [0, calc(100, true), offset(0, 12), offset(-100, -12)]
          break
        case 'bottom-center':
          values = [50, calc(100, true), offset(-50, 0), offset(-100, -12)]
          break
        case 'bottom-right':
          values = [100, calc(100, true), offset(-100, -12), offset(-100, -12)]
          break
      }

      return {
        x: values[0] + '%',
        y: values[1],
        tx: values[2],
        ty: values[3],
      }
    },
    []
  )

  return (
    <AnimatePresence>
      {indexedToasts.map(item => {
        const { top, right, bottom, left } = toastPosition(item.position)

        const [ix, iy] = toastInitial(item.position, item.height)
        const { x, y, tx, ty } = toastTarget(
          item.position,
          item.height,
          item.index
        )

        const initial = { left: ix, top: iy, opacity: 0 }
        const animate = {
          left: x,
          top: y,
          opacity: 1,
        }

        const color = getToastColor(item.type, item.color)
        const background = getToastColor(item.type, item.background)
        const progressBarColor = getToastColor(item.type, item.progressBarColor)
        const icon = toastIcon(item.type, item.icons)

        return (
          <AnimatedToast
            key={item.id}
            initial={initial}
            animate={animate}
            exit={initial}
            style={{
              top,
              right,
              bottom,
              left,
              transform: `translate(${tx}, ${ty})`,
            }}
            width={item.width}
            height={item.height}
            padding={item.contentPadding}
            alignHorizontal={item.contentAlignHorizontal}
            alignVertical={item.contentAlignVertical}
            iconWidth={item.iconWidth}
            color={color}
            background={background}
            borderRadius={item.borderRadius}
            boxShadow={item.boxShadow}
            showIcon={!!icon}
            showCloseIcon={item.showCloseIcon}
          >
            {!!icon && (
              <div className="icon">{toastIcon(item.type, item.icons)}</div>
            )}

            <div className="content">{item.content}</div>

            {item.showCloseIcon && (
              <div className="close-icon">
                <Icon
                  type="light"
                  icon="times"
                  size="1rem"
                  color={color}
                  hoverColor="gray6"
                  onClick={item.close}
                />
              </div>
            )}

            {item.showProgressBar && (
              <ProgressBar
                life={item.life}
                color={progressBarColor}
                height={item.progressBarHeight}
              />
            )}
          </AnimatedToast>
        )
      })}
    </AnimatePresence>
  )
}

export default ToastContainer
